import { Seo } from "../components/Seo";

import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Package, Users, DollarSign, Mail, Pencil, Trash2, Plus, Sparkles } from "lucide-react";

const empty = { title: "", type: "book", price: "", tagline: "", description: "", image_url: "", featured: false, coming_soon: false, release_note: "" };
const emptyArticle = { title: "", excerpt: "", content: "", cover_image: "", category: "Insight", published: true };

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-gold/30 p-6 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-charcoal/60 text-sm">{label}</span>
        <Icon className="w-5 h-5 text-gold" strokeWidth={1.5} />
      </div>
      <p className="font-serif text-4xl text-forest mt-3">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [articles, setArticles] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [aForm, setAForm] = useState(emptyArticle);
  const [aEditing, setAEditing] = useState(null);
  const [aOpen, setAOpen] = useState(false);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data));
    api.get("/contact").then((r) => setMessages(r.data));
    api.get("/waitlist").then((r) => setWaitlist(r.data));
    api.get("/admin/articles").then((r) => setArticles(r.data));
  };

  useEffect(loadAll, []);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ ...p, price: String(p.price) });
    setOpen(true);
  };

  const save = async () => {
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    try {
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated." : "Product created.");
      setOpen(false);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/products/${id}`);
    toast.success("Product deleted.");
    loadAll();
  };

  const openNewArticle = () => { setAEditing(null); setAForm(emptyArticle); setAOpen(true); };
  const openEditArticle = (a) => { setAEditing(a.id); setAForm({ ...a }); setAOpen(true); };
  const saveArticle = async () => {
    try {
      if (aEditing) await api.put(`/articles/${aEditing}`, aForm);
      else await api.post("/articles", aForm);
      toast.success(aEditing ? "Article updated." : "Article published.");
      setAOpen(false);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const removeArticle = async (id) => {
    await api.delete(`/articles/${id}`);
    toast.success("Article deleted.");
    loadAll();
  };


  return (
    <div className="max-w-7xl mx-auto px-6 py-14" data-testid="admin-page">
      <Seo title="Admin Dashboard" description="Laura Combs Productions admin dashboard." path="/admin" noindex />
      <span className="text-gold text-sm uppercase tracking-[0.3em]">Admin</span>
      <h1 className="font-serif text-5xl text-forest mt-3 mb-10">Dashboard</h1>

      {stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-14">
          <StatCard icon={DollarSign} label="Revenue" value={`$${stats.revenue.toFixed(2)}`} />
          <StatCard icon={Package} label="Orders" value={stats.total_orders} />
          <StatCard icon={Users} label="Customers" value={stats.total_users} />
          <StatCard icon={Sparkles} label="Waitlist" value={stats.waitlist_count ?? 0} />
          <StatCard icon={Mail} label="Unread Messages" value={stats.unread_messages} />
        </div>
      )}

      <Tabs defaultValue="products">
        <TabsList className="bg-white border border-gold/30 rounded-full p-1 mb-8">
          <TabsTrigger value="products" className="rounded-full px-6 data-[state=active]:bg-forest data-[state=active]:text-ivory" data-testid="tab-products">Products</TabsTrigger>
          <TabsTrigger value="orders" className="rounded-full px-6 data-[state=active]:bg-forest data-[state=active]:text-ivory" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="messages" className="rounded-full px-6 data-[state=active]:bg-forest data-[state=active]:text-ivory" data-testid="tab-messages">Messages</TabsTrigger>
          <TabsTrigger value="waitlist" className="rounded-full px-6 data-[state=active]:bg-forest data-[state=active]:text-ivory" data-testid="tab-waitlist">Waitlist</TabsTrigger>
          <TabsTrigger value="resources" className="rounded-full px-6 data-[state=active]:bg-forest data-[state=active]:text-ivory" data-testid="tab-resources">Blog</TabsTrigger>
        </TabsList>

        {/* Products */}
        <TabsContent value="products">
          <div className="flex justify-end mb-6">
            <Button onClick={openNew} className="rounded-full bg-forest hover:bg-forest-light text-ivory gap-2" data-testid="add-product-btn">
              <Plus className="w-4 h-4" /> Add Product
            </Button>
          </div>
          <div className="bg-white rounded-2xl border border-gold/30 divide-y divide-gold/20" data-testid="admin-products-list">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-4 p-4">
                <img src={p.image_url || "https://images.unsplash.com/photo-1784032514423-9d700799a19a?crop=entropy&cs=srgb&fm=jpg&q=85&w=200"} alt={p.title} className="w-16 h-16 rounded-xl object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-charcoal font-medium truncate">{p.title}</p>
                  <p className="text-charcoal/50 text-sm capitalize">{p.type} · ${p.price.toFixed(2)}{p.featured ? " · Featured" : ""}{p.coming_soon ? " · Coming Soon" : ""}</p>
                </div>
                <Button variant="ghost" size="icon" aria-label={`Edit ${p.title}`} onClick={() => openEdit(p)} className="text-forest hover:bg-forest/5" data-testid={`edit-${p.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" aria-label={`Delete ${p.title}`} onClick={() => remove(p.id)} className="text-destructive hover:bg-destructive/5" data-testid={`delete-${p.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Orders */}
        <TabsContent value="orders">
          <div className="bg-white rounded-2xl border border-gold/30 divide-y divide-gold/20" data-testid="admin-orders-list">
            {orders.length === 0 ? (
              <p className="p-8 text-charcoal/50 text-center">No orders yet.</p>
            ) : (
              orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between p-5">
                  <div>
                    <p className="text-charcoal font-medium">{o.product_title}</p>
                    <p className="text-charcoal/50 text-sm">{o.user_email} · {new Date(o.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className="text-forest font-medium">${o.amount.toFixed(2)}</span>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Messages */}
        <TabsContent value="messages">
          <div className="space-y-4" data-testid="admin-messages-list">
            {messages.length === 0 ? (
              <p className="bg-white rounded-2xl border border-gold/30 p-8 text-charcoal/50 text-center">No messages yet.</p>
            ) : (
              messages.map((m) => (
                <div key={m.id} className="bg-white rounded-2xl border border-gold/30 p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-charcoal">{m.subject}</p>
                      <p className="text-charcoal/50 text-sm">{m.name} · {m.email}</p>
                    </div>
                    <span className="text-charcoal/40 text-xs">{new Date(m.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-charcoal/70 mt-3 leading-relaxed">{m.message}</p>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Waitlist */}
        <TabsContent value="waitlist">
          <div className="flex items-center justify-between mb-4">
            <p className="text-charcoal/60 text-sm">{waitlist.length} {waitlist.length === 1 ? "person has" : "people have"} joined the community.</p>
            <Button
              variant="outline"
              onClick={() => {
                const csv = "name,email,source,joined\n" + waitlist.map((w) => `${w.name || ""},${w.email},${w.source || ""},${w.created_at}`).join("\n");
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = "waitlist.csv"; a.click();
                URL.revokeObjectURL(url);
              }}
              className="rounded-full border-forest/30 text-forest hover:bg-forest/5 gap-2"
              data-testid="export-waitlist-btn"
            >
              Export CSV
            </Button>
          </div>
          <div className="bg-white rounded-2xl border border-gold/30 divide-y divide-gold/20" data-testid="admin-waitlist-list">
            {waitlist.length === 0 ? (
              <p className="p-8 text-charcoal/50 text-center">No sign-ups yet.</p>
            ) : (
              waitlist.map((w) => (
                <div key={w.id} className="flex items-center justify-between p-5">
                  <div>
                    <p className="text-charcoal font-medium">{w.email}</p>
                    <p className="text-charcoal/50 text-sm">{w.name || "—"} · via {w.source}</p>
                  </div>
                  <span className="text-charcoal/40 text-xs">{new Date(w.created_at).toLocaleDateString()}</span>
                </div>
              ))
            )}
          </div>
        </TabsContent>

        {/* Resources / Articles */}
        <TabsContent value="resources">
          <div className="flex justify-end mb-6">
            <Button onClick={openNewArticle} className="rounded-full bg-forest hover:bg-forest-light text-ivory gap-2" data-testid="add-article-btn">
              <Plus className="w-4 h-4" /> New Article
            </Button>
          </div>
          <div className="bg-white rounded-2xl border border-gold/30 divide-y divide-gold/20" data-testid="admin-articles-list">
            {articles.length === 0 ? (
              <p className="p-8 text-charcoal/50 text-center">No articles yet.</p>
            ) : (
              articles.map((a) => (
                <div key={a.id} className="flex items-center gap-4 p-4">
                  <img src={a.cover_image || "https://images.unsplash.com/photo-1458014854819-1a40aa70211c?crop=entropy&cs=srgb&fm=jpg&q=85&w=200"} alt={a.title} className="w-16 h-16 rounded-xl object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-charcoal font-medium truncate">{a.title}</p>
                    <p className="text-charcoal/50 text-sm">{a.category}{a.published ? "" : " · Draft"}</p>
                  </div>
                  <Button variant="ghost" size="icon" aria-label={`Edit article ${a.title}`} onClick={() => openEditArticle(a)} className="text-forest hover:bg-forest/5" data-testid={`edit-article-${a.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" aria-label={`Delete article ${a.title}`} onClick={() => removeArticle(a.id)} className="text-destructive hover:bg-destructive/5" data-testid={`delete-article-${a.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-gold/40 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl text-forest">{editing ? "Edit Product" : "New Product"}</DialogTitle>
            <DialogDescription className="text-charcoal/60">Fill in the details below to {editing ? "update this" : "add a new"} offering to the store.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-charcoal">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-xl border-gold/40" data-testid="product-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-charcoal">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1 rounded-xl border-gold/40" data-testid="product-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="book">Book</SelectItem>
                    <SelectItem value="course">Course</SelectItem>
                    <SelectItem value="tool">Tool</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-charcoal">Price (USD)</Label>
                <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 rounded-xl border-gold/40" data-testid="product-price-input" />
              </div>
            </div>
            <div>
              <Label className="text-charcoal">Tagline</Label>
              <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="mt-1 rounded-xl border-gold/40" data-testid="product-tagline-input" />
            </div>
            <div>
              <Label className="text-charcoal">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="mt-1 rounded-xl border-gold/40" data-testid="product-description-input" />
            </div>
            <div>
              <Label className="text-charcoal">Image URL</Label>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="mt-1 rounded-xl border-gold/40" data-testid="product-image-input" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="product-featured-switch" />
              <Label className="text-charcoal">Featured on homepage</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.coming_soon} onCheckedChange={(v) => setForm({ ...form, coming_soon: v })} data-testid="product-comingsoon-switch" />
              <Label className="text-charcoal">Coming Soon (not purchasable yet)</Label>
            </div>
            {form.coming_soon && (
              <div>
                <Label className="text-charcoal">Release Note</Label>
                <Input value={form.release_note} onChange={(e) => setForm({ ...form, release_note: e.target.value })} placeholder="e.g. Available September 1, 2026" className="mt-1 rounded-xl border-gold/40" data-testid="product-releasenote-input" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={save} className="rounded-full bg-forest hover:bg-forest-light text-ivory px-8" data-testid="save-product-btn">
              {editing ? "Save Changes" : "Create Product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={aOpen} onOpenChange={setAOpen}>
        <DialogContent className="bg-white border-gold/40 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl text-forest">{aEditing ? "Edit Article" : "New Article"}</DialogTitle>
            <DialogDescription className="text-charcoal/60">Write a short reflection or insight for your Resources page.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-charcoal">Title</Label>
              <Input value={aForm.title} onChange={(e) => setAForm({ ...aForm, title: e.target.value })} className="mt-1 rounded-xl border-gold/40" data-testid="article-title-input" />
            </div>
            <div>
              <Label className="text-charcoal">Category</Label>
              <Input value={aForm.category} onChange={(e) => setAForm({ ...aForm, category: e.target.value })} placeholder="e.g. Personal Growth" className="mt-1 rounded-xl border-gold/40" data-testid="article-category-input" />
            </div>
            <div>
              <Label className="text-charcoal">Cover Image URL</Label>
              <Input value={aForm.cover_image} onChange={(e) => setAForm({ ...aForm, cover_image: e.target.value })} className="mt-1 rounded-xl border-gold/40" data-testid="article-image-input" />
            </div>
            <div>
              <Label className="text-charcoal">Excerpt</Label>
              <Textarea value={aForm.excerpt} onChange={(e) => setAForm({ ...aForm, excerpt: e.target.value })} rows={2} className="mt-1 rounded-xl border-gold/40" data-testid="article-excerpt-input" />
            </div>
            <div>
              <Label className="text-charcoal">Content</Label>
              <Textarea value={aForm.content} onChange={(e) => setAForm({ ...aForm, content: e.target.value })} rows={8} placeholder="Separate paragraphs with a blank line." className="mt-1 rounded-xl border-gold/40" data-testid="article-content-input" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={aForm.published} onCheckedChange={(v) => setAForm({ ...aForm, published: v })} data-testid="article-published-switch" />
              <Label className="text-charcoal">Published (visible on site)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveArticle} className="rounded-full bg-forest hover:bg-forest-light text-ivory px-8" data-testid="save-article-btn">
              {aEditing ? "Save Changes" : "Publish Article"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
